import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    Stack, TextField, Typography,
} from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import AccountAutocomplete from '../common/AccountAutocomplete';
import { AccountService } from '../../services/account.service';
import { EmployeeLoanService } from '../../services/employeeLoan.service';
import { useToast } from '../../context/ToastContext';
import { Account } from '../../interfaces/accounting.interface';
import { IEmployeeLoan, ILoanInstallment, ILoanRepayment } from '../../interfaces/employeeLoan.interface';
import { depositAccountOptions } from '../../utils/postingAccounts';
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';
import { usePermission } from '../../hooks/usePermission';
import { useLoanAccounting } from '../../hooks/useLoanAccounting';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { InstallmentStatusChip, LoanStatusChip, loanPeriod, loanRef, loanTypeLabel } from './loanLabels';

interface Props {
    loanId: string | null;
    onClose: () => void;
    /** Called after any action so the list behind the dialog refreshes. */
    onChanged: () => void;
}

type Mode = null | 'approve' | 'reject' | 'disburse' | 'repay' | 'writeoff';

// W3-D: one loan, its schedule and repayments, and every manager action allowed in its current status.
const EmployeeLoanDetailDialog: React.FC<Props> = ({ loanId, onClose, onChanged }) => {
    const { showSuccess, showApiError } = useToast();
    const canManage = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    const [loan, setLoan] = useState<IEmployeeLoan | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState<Mode>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [accountId, setAccountId] = useState('');
    const [date, setDate] = useState(toLocalIsoDate());
    const [amount, setAmount] = useState('');
    const [reference, setReference] = useState('');
    const [reason, setReason] = useState('');
    const [ovPrincipal, setOvPrincipal] = useState('');
    const [ovCount, setOvCount] = useState('');
    // Accounting off: the loan is a register only, so there is no cash/bank account to pick.
    const { accountingOn } = useLoanAccounting(loan?.institutionId);

    const load = useCallback(async () => {
        if (!loanId) return;
        setLoading(true);
        try {
            setLoan(await EmployeeLoanService.get(loanId));
        } catch (e) {
            showApiError(e, 'Failed to load the loan');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanId]);

    useEffect(() => {
        setMode(null);
        setLoan(null);
        if (loanId) load();
    }, [loanId, load]);

    const openMode = async (m: Mode) => {
        setMode(m);
        setAccountId('');
        setDate(toLocalIsoDate());
        setAmount(m === 'repay' && loan ? String(loan.outstanding) : '');
        setReference('');
        setReason('');
        setOvPrincipal('');
        setOvCount('');
        if ((m === 'disburse' || m === 'repay') && accountingOn && accounts.length === 0) {
            try {
                const assets = await AccountService.getAccountsByType('ASSET');
                setAccounts(Array.isArray(assets) ? depositAccountOptions(assets) : []);
            } catch (e) {
                showApiError(e, 'Failed to load cash and bank accounts');
            }
        }
    };

    const run = async (action: () => Promise<IEmployeeLoan>, done: string) => {
        setBusy(true);
        try {
            const updated = await action();
            setLoan(updated);
            setMode(null);
            showSuccess(done);
            onChanged();
        } catch (e) {
            showApiError(e, 'The action failed');
        } finally {
            setBusy(false);
        }
    };

    const hold = (i: ILoanInstallment) =>
        run(() => EmployeeLoanService.holdInstallment(i.id), 'Installment held and re-added after the last one');

    const submitMode = () => {
        if (!loan) return;
        switch (mode) {
            case 'approve':
                return run(() => EmployeeLoanService.approve(loan.id, {
                    principal: ovPrincipal ? Number(ovPrincipal) : null,
                    installmentCount: ovCount ? Number(ovCount) : null,
                }), 'Loan approved');
            case 'reject':
                return run(() => EmployeeLoanService.reject(loan.id, reason.trim()), 'Request rejected');
            case 'disburse':
                return run(() => EmployeeLoanService.disburse(loan.id, accountingOn ? accountId : null, date),
                    accountingOn ? 'Loan disbursed and journal posted' : 'Loan disbursed');
            case 'repay':
                return run(() => EmployeeLoanService.repay(loan.id, {
                    amount: Number(amount), receiveIntoAccountId: accountingOn ? accountId : null, date, reference: reference.trim() || null,
                }), 'Repayment recorded');
            case 'writeoff':
                return run(() => EmployeeLoanService.writeOff(loan.id, reason.trim()), 'Outstanding balance written off');
            default:
        }
    };

    const submitDisabled = busy
        || (mode === 'reject' && !reason.trim())
        || (mode === 'writeoff' && !reason.trim())
        || (mode === 'disburse' && accountingOn && !accountId)
        || (mode === 'repay' && ((accountingOn && !accountId) || !(Number(amount) > 0)));

    const installmentColumns: Column<ILoanInstallment>[] = [
        { key: 'seq', header: '#', priority: 3, helpKey: 'payroll.loanDetail.inst.col.seq', render: i => i.seq },
        { key: 'due', header: 'Due month', priority: 1, helpKey: 'payroll.loanDetail.inst.col.due', render: i => loanPeriod(i.dueYear, i.dueMonth) },
        { key: 'amt', header: 'Amount', priority: 1, align: 'right', helpKey: 'payroll.loanDetail.inst.col.amt', render: i => formatMoney(i.scheduledAmount) },
        { key: 'rec', header: 'Recovered', priority: 2, align: 'right', helpKey: 'payroll.loanDetail.inst.col.rec', render: i => formatMoney(i.recoveredAmount) },
        { key: 'st', header: 'Status', priority: 2, helpKey: 'payroll.loanDetail.inst.col.st', render: i => <InstallmentStatusChip status={i.status} /> },
        {
            key: 'act', header: 'Actions', helpKey: 'payroll.loanDetail.inst.col.actions', role: 'actions', hideInCard: true,
            render: i => canManage && loan?.status === 'ACTIVE' && i.status === 'SCHEDULED'
                ? <Button data-help="payroll.loanDetail.inst.action.hold" size="small" disabled={busy} onClick={() => hold(i)}>Hold</Button>
                : null,
        },
    ];

    const repaymentColumns: Column<ILoanRepayment>[] = [
        { key: 'date', header: 'Date', priority: 1, helpKey: 'payroll.loanDetail.rep.col.date', render: r => formatDate(r.repaymentDate) },
        { key: 'kind', header: 'Kind', priority: 2, helpKey: 'payroll.loanDetail.rep.col.kind', render: r => (r.kind === 'CASH' ? 'Cash repayment' : 'Final settlement recovery') },
        { key: 'amt', header: 'Amount', priority: 1, align: 'right', helpKey: 'payroll.loanDetail.rep.col.amt', render: r => formatMoney(r.amount) },
        { key: 'ref', header: 'Reference', priority: 3, helpKey: 'payroll.loanDetail.rep.col.ref', render: r => r.reference || '—' },
        { key: 'rev', header: 'Reversed', priority: 3, helpKey: 'payroll.loanDetail.rep.col.rev', render: r => (r.reversedAt ? 'Yes' : '—') },
    ];

    const reclassCount = (loan?.installments ?? []).filter(i => i.reclassNeeded).length;

    return (
        <Dialog open={!!loanId} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{loan ? `${loanTypeLabel(loan.loanType)} ${loanRef(loan.id)} - ${loan.employeeName ?? ''}` : 'Loan'}</DialogTitle>
            <DialogContent dividers data-help-page="payroll.loanDetail.page">
                {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
                {loan && (
                    <>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }} data-help-section="payroll.loanDetail.summary">
                            <Box data-help="payroll.loanDetail.status"><Typography variant="caption" color="text.secondary">Status</Typography><br /><LoanStatusChip status={loan.status} /></Box>
                            <Box data-help="payroll.loanDetail.principal"><Typography variant="caption" color="text.secondary">Principal</Typography><Typography>{formatMoney(loan.principal)}</Typography></Box>
                            <Box data-help="payroll.loanDetail.outstanding"><Typography variant="caption" color="text.secondary">Outstanding</Typography><Typography>{formatMoney(loan.outstanding)}</Typography></Box>
                            <Box data-help="payroll.loanDetail.installments"><Typography variant="caption" color="text.secondary">Installments</Typography>
                                <Typography>{loan.installmentCount} x {formatMoney(loan.installmentAmount)}</Typography></Box>
                            {loan.disbursedDate && <Box data-help="payroll.loanDetail.disbursed"><Typography variant="caption" color="text.secondary">Disbursed</Typography><Typography>{formatDate(loan.disbursedDate)}</Typography></Box>}
                        </Box>
                        {loan.reason && <Typography variant="body2" sx={{ mb: 1 }}>Reason: {loan.reason}</Typography>}
                        {loan.rejectReason && <Alert severity="error" sx={{ mb: 1 }}>Rejected: {loan.rejectReason}</Alert>}
                        {loan.status === 'WRITTEN_OFF' && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                {formatMoney(loan.writtenOffAmount)} written off{loan.writtenOffDate ? ` on ${formatDate(loan.writtenOffDate)}` : ''}: {loan.writeOffReason}
                            </Alert>
                        )}
                        {reclassCount > 0 && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                {reclassCount} deduction(s) were recovered under the legacy payment mode and still need their reclass journal
                                (Employee Loans page, "Recoveries to reclass").
                            </Alert>
                        )}

                        {(loan.installments?.length ?? 0) > 0 && (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Schedule</Typography>
                                <ResponsiveTable data-help-section="payroll.loanDetail.schedule"
                                    pageKey="employee-loan-installments"
                                    columns={installmentColumns}
                                    rows={loan.installments ?? []}
                                    getRowKey={i => i.id}
                                />
                            </>
                        )}
                        {(loan.repayments?.length ?? 0) > 0 && (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Repayments</Typography>
                                <ResponsiveTable data-help-section="payroll.loanDetail.repayments"
                                    pageKey="employee-loan-repayments"
                                    columns={repaymentColumns}
                                    rows={loan.repayments ?? []}
                                    getRowKey={r => r.id}
                                />
                            </>
                        )}

                        {mode && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>{humanizeEnum(mode === 'writeoff' ? 'write_off' : mode)}</Typography>
                                <Stack spacing={2}>
                                    {mode === 'approve' && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                            <TextField data-help="payroll.loanDetail.form.overrideAmount" label="Override amount (optional)" type="number" size="small" value={ovPrincipal}
                                                onChange={e => setOvPrincipal(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 300 }} />
                                            <TextField data-help="payroll.loanDetail.form.overrideInstallments" label="Override installments (optional)" type="number" size="small" value={ovCount}
                                                onChange={e => setOvCount(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 300 }} />
                                        </Box>
                                    )}
                                    {(mode === 'reject' || mode === 'writeoff') && (
                                        <TextField data-help="payroll.loanDetail.form.reason" label="Reason" value={reason} onChange={e => setReason(e.target.value)} required multiline minRows={2}
                                            inputProps={{ maxLength: 500 }} fullWidth />
                                    )}
                                    {(mode === 'disburse' || mode === 'repay') && accountingOn && (
                                        <AccountAutocomplete
                                            data-help="payroll.loanDetail.form.account"
                                            label={mode === 'disburse' ? 'Pay from (cash or bank)' : 'Receive into (cash or bank)'}
                                            accounts={accounts}
                                            value={accountId}
                                            onChange={setAccountId}
                                            required
                                        />
                                    )}
                                    {mode === 'repay' && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                            <TextField data-help="payroll.loanDetail.form.amount" label="Amount" type="number" size="small" value={amount} onChange={e => setAmount(e.target.value)}
                                                sx={{ flex: '1 1 200px', maxWidth: 300 }} />
                                            <TextField data-help="payroll.loanDetail.form.reference" label="Reference" size="small" value={reference} onChange={e => setReference(e.target.value)}
                                                sx={{ flex: '1 1 200px', maxWidth: 300 }} />
                                        </Box>
                                    )}
                                    {(mode === 'disburse' || mode === 'repay') && (
                                        <TextField data-help="payroll.loanDetail.form.date" label="Date" type="date" size="small" value={date} onChange={e => setDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }} sx={{ width: '100%', maxWidth: 300 }} />
                                    )}
                                    {mode === 'writeoff' && (
                                        <Alert severity="warning">
                                            Writes the remaining {formatMoney(loan.outstanding)} off{accountingOn ? ' to the write-off expense account' : ''} and waives the open installments.
                                        </Alert>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button data-help="payroll.loanDetail.action.confirm" variant="contained" onClick={submitMode} disabled={submitDisabled}>Confirm</Button>
                                        <Button data-help="payroll.loanDetail.action.back" onClick={() => setMode(null)} disabled={busy}>Back</Button>
                                    </Box>
                                </Stack>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', px: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {canManage && loan?.status === 'REQUESTED' && !mode && (
                        <>
                            <Button data-help="payroll.loanDetail.action.approve" variant="contained" onClick={() => openMode('approve')} disabled={busy}>Approve</Button>
                            <Button data-help="payroll.loanDetail.action.reject" color="error" onClick={() => openMode('reject')} disabled={busy}>Reject</Button>
                        </>
                    )}
                    {canManage && loan?.status === 'APPROVED' && !mode && !loan.disbursementBatchId && (
                        <Button data-help="payroll.loanDetail.action.disburse" variant="contained" onClick={() => openMode('disburse')} disabled={busy}>Disburse</Button>
                    )}
                    {loan?.status === 'APPROVED' && !mode && loan.disbursementBatchId && (
                        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            In a disbursement batch: disburse it from Disbursement Batches.
                        </Typography>
                    )}
                    {canManage && (loan?.status === 'REQUESTED' || loan?.status === 'APPROVED') && !mode && !loan.disbursementBatchId && (
                        <Button data-help="payroll.loanDetail.action.cancel" color="error" onClick={() => run(() => EmployeeLoanService.cancel(loan.id), 'Loan cancelled')} disabled={busy}>Cancel loan</Button>
                    )}
                    {canManage && loan?.status === 'ACTIVE' && !mode && (
                        <>
                            <Button data-help="payroll.loanDetail.action.repay" variant="outlined" onClick={() => openMode('repay')} disabled={busy}>Record repayment</Button>
                            <Button data-help="payroll.loanDetail.action.writeoff" color="error" onClick={() => openMode('writeoff')} disabled={busy}>Write off</Button>
                        </>
                    )}
                </Box>
                <Button data-help="payroll.loanDetail.action.close" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeLoanDetailDialog;
